<script setup lang="ts">
/**
 * Animated NUMBER display — counts up to the value with an eased tween, and
 * shows an on-brand shimmer placeholder while loading. Built for the monetary
 * figures (household AUM / market value, totals, returns) that currently snap
 * in or sit at 0 while data loads.
 *
 *   <AnimatedNumber :value="aum" :loading="pending" />               <!-- $1,234,567 -->
 *   <AnimatedNumber :value="pct" format="percent" :decimals="2" />   <!-- 12.34% -->
 *   <AnimatedNumber :value="count" format="decimal" />               <!-- 1,234 -->
 *
 * TABLE CELLS pass their own `formatter` so the count-up doesn't quietly
 * relabel the number on its way in. The portal's tables are accounting-styled
 * — negatives in parentheses, null as `---` (see utils/table-format.ts) — and
 * Intl's plain currency output disagrees with both. The formatter keeps the
 * column's own rendering and only the VALUE animates:
 *
 *   <AnimatedNumber :value="row.MarketValue" :formatter="formatCurrencyAccountingUSD" />
 *
 * `delay` staggers a group (see staggerDelay in useCountUpTicker) and
 * `animate: false` renders the final value immediately — used to stop a table
 * re-counting every time it is sorted or filtered.
 *
 * Respects reduce-motion (snaps instead of tweening) and is SSR-safe (renders
 * the final formatted value on the server, no 0-flash). The count-up only runs
 * client-side, off the app-wide ticker in useCountUpTicker.
 */
import { computed, ref, watch, onUnmounted } from 'vue'
import { startCountUp, type CountUpHandle } from '~/composables/useCountUpTicker'

const props = withDefaults(defineProps<{
  value?: number | string | null
  loading?: boolean
  format?: 'currency' | 'decimal' | 'percent'
  currency?: string
  /** BCP-47 locale for the built-in formatter. Defaults to the viewer's, which
   *  is right for the headline figures; pass 'en-US' where the surrounding
   *  markup already hardcodes it (the sidebar, every table column) so one
   *  number doesn't switch separators on a non-US browser. */
  locale?: string
  decimals?: number
  duration?: number
  prefix?: string
  suffix?: string
  /** Render the tweened value yourself — overrides format/currency/decimals.
   *  Lets a table cell keep its accounting formatter while still counting. */
  formatter?: (value: number) => string
  /** Milliseconds before this instance starts moving, for staggered groups. */
  delay?: number
  /** false → show the final value at once (no tween). */
  animate?: boolean
  /** Tailwind width for the loading shimmer, sized to the expected number. */
  skeletonWidth?: string
}>(), {
  format: 'currency',
  currency: 'USD',
  decimals: 0,
  duration: 420,
  prefix: '',
  suffix: '',
  delay: 0,
  animate: true,
  skeletonWidth: 'w-28'
})

// Currency/value fields arrive as either number or numeric string — coerce.
const numeric = computed<number | null>(() => {
  const v = props.value
  const n = typeof v === 'string' ? Number(v) : v
  return n != null && Number.isFinite(n) ? n : null
})
const hasValue = computed(() => numeric.value != null)

// A custom formatter owns the empty case too (tables print `---`, not a
// shimmer, for a null cell), so only the default rendering falls back to the
// skeleton when there is no number yet.
const isLoading = computed(() =>
  props.formatter ? !!props.loading : props.loading || !hasValue.value
)

// Server renders the final value directly (no animation, no 0-flash); the
// client starts at 0 so the first reveal counts up.
const displayed = ref(import.meta.server && numeric.value != null ? numeric.value : 0)
let handle: CountUpHandle | null = null

function animateTo(target: number) {
  handle?.cancel()
  handle = startCountUp({
    from: displayed.value,
    to: target,
    duration: props.animate ? props.duration : 0,
    delay: props.animate ? props.delay : 0,
    onFrame: (v) => { displayed.value = v }
  })
}

watch(
  () => [numeric.value, props.loading] as const,
  () => { if (numeric.value != null && !props.loading) animateTo(numeric.value) },
  { immediate: true }
)

onUnmounted(() => handle?.cancel())

const formatter = computed(() => {
  const opts: Intl.NumberFormatOptions = {
    minimumFractionDigits: props.decimals,
    maximumFractionDigits: props.decimals
  }
  if (props.format === 'currency') { opts.style = 'currency'; opts.currency = props.currency }
  return new Intl.NumberFormat(props.locale, opts)
})

const text = computed(() => {
  // No number to show: hand the raw value to a custom formatter so it can
  // print the column's own placeholder, otherwise the skeleton covers it.
  if (!hasValue.value && props.formatter) return props.formatter(props.value as never)
  if (props.formatter) return props.formatter(displayed.value)

  const core = formatter.value.format(displayed.value)
  const pct = props.format === 'percent' ? '%' : ''
  return `${props.prefix}${core}${pct}${props.suffix}`
})
</script>

<template>
  <AppSkeleton v-if="isLoading" :width="skeletonWidth" height="h-[1em]" rounded="rounded-md" />
  <span v-else class="tabular-nums">{{ text }}</span>
</template>
